/**
 * Sauvegarde automatique du dashboard + état exposé à l'UI.
 *
 * - `requestSave()` : sérialise la grille, déduplique, et planifie une sauvegarde
 *   debouncée (regroupe les events d'un même geste).
 * - `status` : idle | pending | saving | saved | error → indicateur visuel discret.
 * - flush `pagehide` : envoi best-effort (keepalive) si une modif n'est pas encore
 *   partie → évite la perte lors d'un F5 rapide.
 * - `flushNow()` : envoi immédiat (utilisé par « Terminer »).
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { GridItemHTMLElement, GridStackWidget } from "gridstack";
import { flushDashboardConfig, saveDashboardConfig } from "../../../api/dashboard";
import { useGridStackContext } from "../../lib";
import { DashboardConfig, serializeDashboard } from "./dashboardConfig";

export type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

type DashboardSaveValue = {
  status: SaveStatus;
  requestSave: () => void;
  flushNow: () => Promise<void>;
};

const DEBOUNCE_MS = 800;
const SAVED_VISIBLE_MS = 2000;

type Rect = { x: number; y: number; w: number; h: number };

const DashboardSaveContext = createContext<DashboardSaveValue | null>(null);

export function useDashboardSave(): DashboardSaveValue {
  return (
    useContext(DashboardSaveContext) ?? {
      status: "idle",
      requestSave: () => undefined,
      flushNow: async () => undefined,
    }
  );
}

export function DashboardSaveProvider({
  baselineJson,
  children,
}: PropsWithChildren<{ baselineJson: string }>) {
  const { gridStack, saveOptions } = useGridStackContext();
  const [status, setStatus] = useState<SaveStatus>("idle");

  const lastSavedJson = useRef(baselineJson);
  const pendingConfig = useRef<DashboardConfig | null>(null);
  const pendingJson = useRef<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  const savedReset = useRef<ReturnType<typeof setTimeout>>();
  // Drag : true pendant un déplacement (pour ne pas sauvegarder les états transitoires) ;
  // snapshot des positions au début du drag pour restaurer les widgets non déplacés.
  const dragging = useRef(false);
  const dragSnapshot = useRef<Map<string, Rect>>(new Map());

  const runSave = useCallback(async () => {
    const config = pendingConfig.current;
    const json = pendingJson.current;
    if (!config || json === null) return;

    setStatus("saving");
    try {
      await saveDashboardConfig(config);
      lastSavedJson.current = json;
      pendingConfig.current = null;
      pendingJson.current = null;
      setStatus("saved");
      clearTimeout(savedReset.current);
      savedReset.current = setTimeout(
        () => setStatus((s) => (s === "saved" ? "idle" : s)),
        SAVED_VISIBLE_MS
      );
    } catch {
      setStatus("error");
    }
  }, []);

  const requestSave = useCallback(() => {
    if (!gridStack) return;
    const config = serializeDashboard(saveOptions());
    const json = JSON.stringify(config);
    if (json === lastSavedJson.current) return; // rien de neuf à enregistrer

    pendingConfig.current = config;
    pendingJson.current = json;
    setStatus("pending");
    clearTimeout(debounce.current);
    debounce.current = setTimeout(runSave, DEBOUNCE_MS);
  }, [gridStack, saveOptions, runSave]);

  const flushNow = useCallback(async () => {
    clearTimeout(debounce.current);
    if (pendingConfig.current && pendingJson.current !== lastSavedJson.current) {
      await runSave();
    }
  }, [runSave]);

  // Abonnement aux événements GridStack (déplacement / resize / ajout / suppression).
  useEffect(() => {
    if (!gridStack) return;

    const snapshotPositions = () => {
      const map = new Map<string, Rect>();
      (gridStack.save(false, false) as GridStackWidget[]).forEach((node) => {
        if (node.id != null) {
          map.set(String(node.id), {
            x: node.x ?? 0,
            y: node.y ?? 0,
            w: node.w ?? 1,
            h: node.h ?? 1,
          });
        }
      });
      return map;
    };

    // Pendant le drag, on ne sauvegarde pas (états transitoires) ; le save final
    // est déclenché au lâcher, après restauration.
    const onChange = () => {
      if (!dragging.current) requestSave();
    };
    const onAddedOrRemoved = () => requestSave();

    const onDragStart = () => {
      dragging.current = true;
      dragSnapshot.current = snapshotPositions();
    };

    const onDragStop = (_event: Event, el: GridItemHTMLElement) => {
      const dragged = el.gridstackNode;
      const draggedId = dragged?.id != null ? String(dragged.id) : null;
      // Position finale voulue (capturée AVANT la remise à plat).
      const finalX = dragged?.x;
      const finalY = dragged?.y;

      // Phase 1 : on remet TOUS les widgets (y compris celui déplacé) à leur
      // position d'origine → on repart d'un layout propre, sans collision ni
      // artefact de trajet. (Synchrone : pas de scintillement avant le repaint.)
      gridStack.batchUpdate();
      dragSnapshot.current.forEach((snap, id) => {
        const itemEl = gridStack.el?.querySelector<GridItemHTMLElement>(
          `[gs-id="${id}"]`
        );
        if (itemEl) gridStack.update(itemEl, { x: snap.x, y: snap.y });
      });
      gridStack.batchUpdate(false);

      // Phase 2 : on déplace UNIQUEMENT le widget lâché à sa position finale →
      // GridStack ne pousse que le minimum nécessaire (pas d'escalade).
      if (draggedId && finalX !== undefined && finalY !== undefined) {
        gridStack.update(el, { x: finalX, y: finalY });
      }

      dragging.current = false;
      requestSave();
    };

    gridStack.on("change", onChange);
    gridStack.on("added", onAddedOrRemoved);
    gridStack.on("removed", onAddedOrRemoved);
    gridStack.on("dragstart", onDragStart);
    gridStack.on("dragstop", onDragStop);

    return () => {
      gridStack.off("change");
      gridStack.off("added");
      gridStack.off("removed");
      gridStack.off("dragstart");
      gridStack.off("dragstop");
    };
  }, [gridStack, requestSave]);

  // Filet de sécurité au déchargement : envoi keepalive de la modif en attente.
  useEffect(() => {
    const onHide = () => {
      if (
        pendingConfig.current &&
        pendingJson.current !== lastSavedJson.current
      ) {
        flushDashboardConfig(pendingConfig.current);
        lastSavedJson.current = pendingJson.current as string;
      }
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(debounce.current);
      clearTimeout(savedReset.current);
    },
    []
  );

  return (
    <DashboardSaveContext.Provider value={{ status, requestSave, flushNow }}>
      {children}
    </DashboardSaveContext.Provider>
  );
}
